import React from 'react';
import JSON5 from 'json5';
import styled from 'styled-components';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TrueImpactConfirmationDialog from 'shared/design-system/components/TrueImpactConfirmationDialog';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';

interface OverwriteProgramAnswerModalProps {
  open: boolean;
  /**
   * Optional question label shown above the two-answer comparison.
   * Accepts a React node so callers can pass `question.textNode` — a
   * pre-rendered fragment with `<Program_name>` / beneficiary
   * placeholders already replaced (see `applyProgramTagsToQuestions`
   * and `replaceQuestionText`).
   */
  questionTitle?: React.ReactNode;
  /** Current program-profile answer that would be replaced. */
  programAnswerText: string;
  /** Answer just saved on the report; the value we'd copy up. */
  reportAnswerText: string;
  /**
   * Fine-grained loading/error state for the program-level save. When
   * `REQUEST_START` we lock both buttons and show a spinner on Overwrite;
   * `REQUEST_ERROR` surfaces the message below the actions.
   */
  savingState?: RequestState<unknown>;
  onSkip: () => void;
  onOverwrite: () => void;
}

function renderAnswer(text: string, emptyLabel: string): React.ReactNode {
  if (!text) return <em>{emptyLabel}</em>;
  const trimmed = text.trim();
  if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
    try {
      const parsed = JSON5.parse(trimmed);
      if (
        Array.isArray(parsed) &&
        parsed.every(item => typeof item === 'string')
      ) {
        if (parsed.length === 0) return <em>{emptyLabel}</em>;
        return (
          <BulletList>
            {parsed.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </BulletList>
        );
      }
    } catch {
      // fall through to plain text
    }
  }
  return text;
}

/**
 * Confirmation shown after the user saves a report-level answer that
 * differs from the shared program-profile answer. Explains the choice
 * side-by-side (program vs. new report) and lets the user either keep
 * the program answer untouched ("Skip") or push the new value up to the
 * program profile ("Overwrite"). Either choice advances the wizard —
 * the caller is expected to run its normal continue logic once the
 * modal closes.
 */
const OverwriteProgramAnswerModal: React.FC<OverwriteProgramAnswerModalProps> = ({
  open,
  questionTitle,
  programAnswerText,
  reportAnswerText,
  savingState,
  onSkip,
  onOverwrite,
}) => {
  const isSaving = savingState?.type === 'REQUEST_START';
  const errorText =
    savingState?.type === 'REQUEST_ERROR'
      ? savingState.error?.message ?? 'Failed to update the program answer.'
      : null;

  return (
    <TrueImpactConfirmationDialog
      open={open}
      handleClose={isSaving ? () => undefined : onSkip}
      maxWidth="900px"
      previous={{
        text: 'Keep as it is',
        onClick: onSkip,
        disabled: isSaving,
      }}
      next={{
        text: 'Overwrite program answer',
        onClick: onOverwrite,
        isLoading: isSaving,
        disabled: isSaving,
      }}
    >
      <Body>
        <TrueImpactText variant="heading" align="center">
          Update your program answer?
        </TrueImpactText>
        <TrueImpactText variant="body" align="center" color="greyDark">
          Your new response differs from what&apos;s currently saved on your
          program profile. Overwriting will replace the program answer with the
          one you just entered.
        </TrueImpactText>

        {questionTitle && (
          <QuestionLabel>
            <TrueImpactText variant="body-small" color="greyDark">
              {questionTitle}
            </TrueImpactText>
          </QuestionLabel>
        )}

        <Comparison>
          <AnswerCard>
            <CardKicker>
              <TrueImpactText variant="body-small" color="greyDark">
                Current program answer
              </TrueImpactText>
            </CardKicker>
            <AnswerText>
              <TrueImpactText variant="body">
                {renderAnswer(programAnswerText, 'No answer yet')}
              </TrueImpactText>
            </AnswerText>
          </AnswerCard>

          <Arrow>
            <FontAwesomeIcon icon={faArrowRight} />
          </Arrow>

          <AnswerCard highlight={true}>
            <CardKicker>
              <TrueImpactText variant="body-small" color="ctaPrimary">
                Your new program answer
              </TrueImpactText>
            </CardKicker>
            <AnswerText>
              <TrueImpactText variant="body">
                {renderAnswer(reportAnswerText, 'Empty')}
              </TrueImpactText>
            </AnswerText>
          </AnswerCard>
        </Comparison>

        {errorText && (
          <TrueImpactText variant="body-small" color="critical" align="center">
            {errorText}
          </TrueImpactText>
        )}
      </Body>
    </TrueImpactConfirmationDialog>
  );
};

export default OverwriteProgramAnswerModal;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.small};
`;

const QuestionLabel = styled.div`
  text-align: center;
  margin-top: ${SPACERS.tiny};
`;

const Comparison = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: stretch;
  gap: ${SPACERS.small};
  margin-top: ${SPACERS.small};
`;

const AnswerCard = styled.div<{ highlight?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.tiny};
  padding: ${SPACERS.small};
  border-radius: 8px;
  border: 1px solid
    ${({ highlight }) => (highlight ? COLORS.ctaPrimary : COLORS.greyLight)};
  background-color: ${({ highlight }) =>
    highlight ? COLORS.ctaPrimaryBackground : COLORS.whiteBase};
  min-height: 96px;
`;

const CardKicker = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const AnswerText = styled.div`
  word-break: break-word;
  em {
    color: ${COLORS.greyDark};
    font-style: italic;
  }
`;

const BulletList = styled.ul`
  margin: 0;
  padding-left: 1.25em;
  li {
    margin: 0;
  }
`;

const Arrow = styled.div`
  display: flex;
  align-items: center;
  color: ${COLORS.greyDark};
`;
